import React from 'react';
import { ArrowLeft } from 'lucide-react';
import MarketingHeader from '../components/MarketingHeader';
import { REQUEST_ACCESS_URL } from '../config/links';
import LegalFooter from '../components/LegalFooter';
import useDocumentMeta from '../hooks/useDocumentMeta';

/* One page that consolidates every pricing/program rule that previously
   lived scattered across card footnotes and audience pages. The answers
   here are the canonical statements — if a rule changes, change it here
   and wherever it's echoed. */

const FAQS = [
  {
    q: 'What is a node?',
    a: 'A node is a part, sub-assembly, assembly, or product — with its full engineering record attached: requirements, decisions, rejected alternatives, analysis, test results, and revision history. If you\'d give it a part number, it\'s a node.',
  },
  {
    q: 'What counts against my 100 free nodes?',
    a: 'Only nodes — distinct parts, sub-assemblies, assemblies, or products. Revisions are unlimited and never count against your total. A bracket that goes through six revisions is still one node.',
  },
  {
    q: 'What happens when I reach 100 nodes?',
    a: 'Your record goes read-only until you pick a plan. Nothing is ever deleted — every node, decision, and test result stays exactly where you left it, and everything unlocks the moment you upgrade.',
  },
  {
    q: 'Is there a limit to how many nodes Team includes?',
    a: 'Team scales to 2,500 nodes, and there\'s no per-node charge — we never meter capture. If your organization grows past 2,500 nodes, you\'ve reached the scale where Enterprise\'s controls and support fit, and we\'ll move you over. (The free tier\'s first 100 nodes are a separate free allowance.)',
  },
  {
    q: 'Is the free tier a trial?',
    a: 'No. There is no 30-day timer and no card required. The first 100 nodes are free for every organization, for as long as you need them — an allowance sized to a real first hardware program, not a countdown.',
  },
  {
    q: 'Who qualifies for the $50 Startup Credit?',
    a: 'Solo founders and qualified teams with fewer than five engineers pay $50/user/month instead of $250 — the full Team tier, not a reduced version. Request access and tell us about your team; qualification is confirmed during onboarding.',
  },
  {
    q: 'How does the free .edu tier work?',
    a: 'University teams get the full Team tier free with a .edu email — free for every member of your team, no card, no time limit. Built for competition teams where knowledge transfer between graduating classes is the whole game.',
  },
  {
    q: 'How does the free notebook work?',
    a: 'The Factory OS notebook is graph paper with a QR code on every page. Sketch a part, scan the code with your phone, and the page runs through our OCR pipeline and opens in the app as a structured node — material, requirement, open questions — connected to everything else you\'ve captured. Request one and we\'ll mail it to you.',
  },
  {
    q: 'Who owns the data I put into Factory OS?',
    a: 'You do. Projects are private by default, and your designs, decisions, and test data are your IP — we host it, we don\'t own it. Export and deletion requests are honored via privacy@factory-os.com. See the Security & trust page for the longer answer.',
  },
  {
    q: 'How does the machining program work?',
    a: 'Complete a full design cycle on the platform — requirements, CAD, DFM review, analysis, and a Design → Build → Prove record — and our in-house shop can fabricate the part. FDM 3D printing is free for anyone on the platform; CNC milling and more processes are coming online. Selection is merit-based and capacity is limited.',
  },
  {
    q: 'What does Enterprise add?',
    a: 'Everything in Team, plus SSO, security reviews, deployment controls, integrations, onboarding, and dedicated support — priced for your organization. Email hello@factory-os.com with "Enterprise" in the subject to set up a call.',
  },
  {
    q: 'Can we use Factory OS in an ITAR or export-controlled environment?',
    a: 'For restricted and controlled customers, we offer an ITAR-aligned deployment option on the Enterprise tier, available on request: AI inference and embeddings run on models you host inside your own boundary, so controlled technical data never leaves your environment — while your team still works in Factory OS. We\'re building this with design partners and don\'t claim certifications we don\'t hold; email hello@factory-os.com with "Enterprise" in the subject and your requirements, and we\'ll scope it with you.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const FAQPage = () => {

  useDocumentMeta({
    title: 'FAQ — Nodes, free tier, notebooks, and programs | Factory OS',
    description:
      'What a node is, what counts against the 100 free nodes, what happens at the limit, who qualifies for startup and .edu pricing, how the free notebook and machining programs work, and who owns your data.',
    path: '/faq',
  });

  return (
    <div className="fos-notebook min-h-screen">
      <MarketingHeader />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <section className="pt-14 sm:pt-20 pb-12 px-5">
        <div className="max-w-[820px] mx-auto">
          <a href="/" className="inline-flex items-center gap-2 no-underline mb-8" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
            <ArrowLeft size={14} /> Back to Factory OS
          </a>

          <div className="nb-eyebrow" style={{ color: 'var(--nb-accent)' }}>frequently asked</div>
          <h1 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.05, color: 'var(--ink)', letterSpacing: '-0.5px' }}>
            The rules, in one place.
          </h1>
          <p className="mt-3 max-w-[640px]" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.45 }}>
            Nodes, the free tier, qualification, the notebook, and what happens to your data — the canonical answers.
          </p>

          <div className="mt-9 flex flex-col gap-3">
            {FAQS.map(({ q, a }) => (
              <details key={q} className="nb-sketch-thin" style={{ padding: '14px 16px' }}>
                <summary style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--ink)', cursor: 'pointer', listStylePosition: 'outside' }}>
                  {q}
                </summary>
                <p className="mt-3" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.5 }}>
                  {a}
                </p>
              </details>
            ))}
          </div>

          <div className="nb-sticky mt-8" style={{ transform: 'rotate(-0.4deg)' }}>
            <b>Still have a question?</b> Email <a href="mailto:hello@factory-os.com" style={{ color: 'var(--nb-accent)' }}>hello@factory-os.com</a> — a person reads every message.
          </div>

          <div className="flex flex-wrap gap-3 mt-8">
            <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline text-center">Request access</a>
            <a href="/pricing" className="nb-btn nb-btn-ghost">See pricing</a>
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default FAQPage;
